import {
  Stack,
  Fade,
  List,
  Pagination,
  Avatar,
  Box,
  CardHeader,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  ListItem,
} from "@mui/material";
import LoadingSpinner from "../../App/Components/LoadingSpinner/LoadingSpinner";
import { IMessageCampaign } from "../../App/Typings/Campaigns";
import { IPagination } from "../../App/Typings/MessageText";
import { useState } from "react";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";

export interface CampaignListingProps {
  campaignsLoaded: boolean;
  campaigns: IPagination<IMessageCampaign> | undefined;
  onDelete: (id: string) => void;
  page: number;
  onPageChange: (page: number) => void;
}

export default function CampaignListing({
  campaignsLoaded,
  campaigns,
  onDelete,
  page,
  onPageChange,
}: CampaignListingProps) {
  return (
    <>
      {campaignsLoaded ? (
        <Fade in timeout={250}>
          <Stack direction="column" alignItems="stretch" sx={{ height: "100%" }}>
            <List sx={{ flexGrow: { lg: 1 } }}>
              {campaigns && campaigns.items.length > 0 ? (
                campaigns?.items?.map((c, idx) => (
                  <ListItemButtonItem
                    key={`message-campaign-item-${idx}`}
                    onDelete={onDelete}
                    index={idx}
                    campaign={c}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    minHeight: 300,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6">You do not have any Email Campaigns.</Typography>
                </Box>
              )}
            </List>
            <Pagination
              page={page}
              onChange={(_ev, p) => onPageChange(p)}
              sx={{ alignSelf: "center" }}
              count={campaigns?.totalPages ?? 1}
              color="primary"
            />
          </Stack>
        </Fade>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
}

const ListItemButtonItem = ({
  index,
  campaign,
  onDelete,
}: {
  index: number;
  campaign: IMessageCampaign;
  onDelete: (id: string) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { getUserAdjustedDate } = useUserProfileStore();

  return (
    <ListItem
      sx={{
        border: "1px solid #ccc",
        borderRadius: 1,
        margin: "12px",
        backgroundColor: "#fff",
        minHeight: "75px",
      }}
      key={`message-list-item-${index}`}
    >
      <Stack
        direction="row"
        flexWrap="wrap"
        sx={{ width: "100%", height: "100%" }}
        justifyContent={"space-around"}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <CardHeader
          sx={{ flexGrow: 1, minWidth: "45%" }}
          avatar={
            <Avatar sx={{ backgroundColor: "primary.main" }} title={"Sent"}>
              <EmailIcon />
            </Avatar>
          }
          title={<Typography sx={{ fontWeight: 500 }}>{campaign.name}</Typography>}
          subheader={
            <Typography variant="caption">{`Created on ${getUserAdjustedDate(campaign.createdDate).format(
              "MMM DD, YYYY - h:mm a"
            )}`}</Typography>
          }
        />
        <Stack
          direction="column"
          spacing={0}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ flexGrow: 1 }}
        >
          <Typography fontSize={12}>Opens</Typography>
          <Typography
            sx={{
              fontWeight: 600,
            }}
            fontSize={12}
          >
            {campaign.opens}
          </Typography>
        </Stack>
        <Stack
          direction="column"
          spacing={0}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ flexGrow: 1 }}
        >
          <Typography fontSize={12}>Clicks</Typography>
          <Typography
            sx={{
              fontWeight: 600,
            }}
            fontSize={12}
          >
            {campaign.clicks}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems={"center"} justifyContent={"space-evenly"} sx={{ flexGrow: 1 }}>
          <Stack
            direction="column"
            spacing={0}
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ flexGrow: 1 }}
          >
            <Typography fontSize={12}>Unsubscribe</Typography>
            <Typography
              sx={{
                fontWeight: 600,
              }}
              fontSize={12}
            >
              {campaign.unsubscribes}
            </Typography>
          </Stack>
          <Box>
            <IconButton
              sx={{ flexGrow: 1 }}
              id="message-campaign-options-button"
              aria-controls={open ? "message-campaign-options-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={(ev) => handleClick(ev)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="message-campaign-options-menu"
              aria-labelledby="message-campaign-options-button"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <MenuItem
                onClick={() => {
                  onDelete(campaign.id);
                  handleClose();
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </Box>
        </Stack>
      </Stack>
    </ListItem>
  );
};
